import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import Box from '@mui/material/Box/Box';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import AkomiDialog from './AkomiDialog';
import AkomiDialogActions from './AkomiDialogActions';

interface AvailabilityCounts {
  available: number;
  total: number;
}

interface FormatOption {
  value: string;
  label: string;
}

interface FormatItem {
  value: string;
  label: string;
  counts: string | null;
  disabled: boolean;
}

interface ConfirmPayload {
  formatType: string | null;
}

interface BatchDownloadDialogProps {
  isOpen: boolean;
  availability?: Record<string, AvailabilityCounts> | null;
  options?: FormatOption[] | null;
  defaultFormat?: string;
  loading?: boolean;
  onConfirm: (payload: ConfirmPayload) => void;
  onCancel: () => void;
}

/**
 * BatchDownloadDialog
 * UI-only dialog for selecting a uniform format and fallback policy for multi-asset downloads.
 * Feature-flag guarded by isBatchDownloadEnabled() at call sites.
 */
const BatchDownloadDialog: React.FC<BatchDownloadDialogProps> = ({
  isOpen,
  availability = null,
  options = null,
  defaultFormat = 'PR',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [formatType, setFormatType] = useState<string | null>(defaultFormat);

  // Helper to read availability for a given code robustly - stable since availability is the only dependency
  const availFor = useMemo(() => {
    return (code: string | null): AvailabilityCounts | null => {
      if (!availability || !code) return null;
      const k = String(code);
      return availability[k] || availability[k.toUpperCase()] || availability[k.toLowerCase()] || null;
    };
  }, [availability]);

  const items = useMemo<FormatItem[]>(() => {
    // If options are explicitly provided by caller and empty, do NOT fall back to legacy defaults.
    if (Array.isArray(options)) {
      if (options.length === 0) return [];
      return options.map((opt) => {
        const keys = [opt.value, String(opt.value).toUpperCase(), String(opt.value).toLowerCase()];
        const countsObj = availability ? availability[keys[0]] || availability[keys[1]] || availability[keys[2]] : null;
        const counts = countsObj ? `${countsObj.available} / ${countsObj.total}` : null;
        const disabled = countsObj ? countsObj.available === 0 : false;
        return { value: opt.value, label: opt.label || opt.value, counts, disabled };
      });
    }
    // No explicit options provided: fallback to proxy/hires legacy options
    const data: FormatItem[] = [
      {
        value: 'PR',
        label: 'Proxy',
        counts: availability?.PR ? `${availability.PR.available} / ${availability.PR.total}` : null,
        disabled: availability?.PR ? availability.PR.available === 0 : false,
      },
      {
        value: 'HR',
        label: 'HiRes',
        counts: availability?.HR ? `${availability.HR.available} / ${availability.HR.total}` : null,
        disabled: availability?.HR ? availability.HR.available === 0 : false,
      },
    ];
    return data;
  }, [options, availability]);

  // Helper: choose default format code based on availability and options - stable since availFor and options are dependencies
  const chooseDefaultFormat = useMemo(() => {
    return (): string | null => {
      if (!Array.isArray(options) || options.length === 0) return null;
      // Prefer Proxy (PR) if available > 0
      const proxyOpt = options.find(
        (o) =>
          o.label?.toLowerCase?.() === 'proxy' ||
          String(o.value).toUpperCase() === 'PR' ||
          String(o.value).toLowerCase() === 'proxy',
      );
      if (proxyOpt && (availFor(proxyOpt.value)?.available || 0) > 0) return proxyOpt.value;
      // Else prefer HiRes (HR) if available > 0
      const hiresOpt = options.find(
        (o) =>
          o.label?.toLowerCase?.() === 'hires' ||
          String(o.value).toUpperCase() === 'HR' ||
          String(o.value).toLowerCase() === 'hires',
      );
      if (hiresOpt && (availFor(hiresOpt.value)?.available || 0) > 0) return hiresOpt.value;
      // Else first available > 0
      const firstAvailable = options.find((o) => (availFor(o.value)?.available || 0) > 0);
      if (firstAvailable) return firstAvailable.value;
      // Else nothing available; return null
      return null;
    };
  }, [availFor, options]);

  // Single effect: on open, compute and set default; on close/unmount, reset to null
  useEffect(() => {
    if (isOpen) {
      const next = chooseDefaultFormat();
      setFormatType(next);
    }
    return () => {
      setFormatType(null);
    };
  }, [isOpen, chooseDefaultFormat]);

  const noOptionsAvailable = Array.isArray(options) && options.length === 0;
  const allDisabled = items.length > 0 && items.every((i) => i.disabled);
  const canConfirm = useMemo(() => {
    if (noOptionsAvailable || allDisabled) return false;
    if (!formatType) return false;
    if (!availability) return true;
    const sel = availFor(formatType);
    return sel ? sel.available > 0 : true;
  }, [formatType, availability, noOptionsAvailable, allDisabled, availFor]);
  const selectedAvailability = availability && formatType ? availability[formatType] : null;
  const hasPartial = selectedAvailability
    ? selectedAvailability.available > 0 && selectedAvailability.available < selectedAvailability.total
    : false;
  const isLoading = Boolean(loading || (isOpen && availability === null));

  const handleFormatChange = (e: SelectChangeEvent<string | null>): void => {
    setFormatType(e.target.value as string | null);
  };

  return (
    <AkomiDialog
      open={isOpen}
      onClose={onCancel}
      title="Choose Format"
      maxWidth="xs"
      loading={isLoading}
      skeletonRows={3}
      variant="form"
      actions={
        <AkomiDialogActions
          variant="cancel-confirm"
          primaryLabel="Download"
          onPrimary={() => onConfirm({ formatType })}
          onSecondary={onCancel}
          primaryDisabled={!canConfirm || isLoading}
          loading={isLoading}
        />
      }
    >
      {noOptionsAvailable || allDisabled ? (
        <Box sx={{ minHeight: 96, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No downloadable formats available for this selection.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <FormLabel sx={{ display: 'block', fontSize: '13px', color: 'text.secondary', mb: '6px' }}>
            Select a file format
          </FormLabel>
          <Select fullWidth size="small" value={formatType ?? ''} onChange={handleFormatChange} disabled={isLoading}>
            {items.map((item) => (
              <MenuItem key={item.value} value={item.value} disabled={item.disabled || isLoading}>
                {item.disabled && <OfflineBoltIcon sx={{ pr: '7px' }} />}
                {item.label}
                {item.counts ? ` (${item.counts} available)` : ''}
              </MenuItem>
            ))}
          </Select>
          {selectedAvailability && (
            <Typography variant="caption" color="textSecondary">
              Availability: {selectedAvailability.available} / {selectedAvailability.total}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="textSecondary">
          Selection applies uniformly to all items. ZIP packaging is not used; files download individually.
        </Typography>
        {hasPartial && (
          <Typography variant="caption" color="error">
            Some selected items do not have the chosen format. These will be skipped.
          </Typography>
        )}
      </Box>
    </AkomiDialog>
  );
};

export default BatchDownloadDialog;
